import React, { useEffect, useState, useCallback } from "react";
import { AlertTriangle, ChevronRight, CheckCircle2, ShieldAlert } from "lucide-react";
import api from "../lib/api";

const PRIORITY = {
  high: { dot: "#EF4444", label: "ACTION REQUIRED", ring: "border-[#EF4444]/50", chip: "bg-[#EF4444]/15 text-[#F28B8B]" },
  medium: { dot: "#F59E0B", label: "ACTION REQUIRED", ring: "border-[#F59E0B]/40", chip: "bg-[#F59E0B]/15 text-[#E7B053]" },
  low: { dot: "#EAB308", label: "ROUTINE", ring: "border-[#EAB308]/30", chip: "bg-[#EAB308]/12 text-[#D6C45A]" },
};

export default function AdminActionCenter({ onNavigate }) {
  const [tasks, setTasks] = useState(null);
  const [acknowledged, setAcknowledged] = useState({});

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/action-center"); setTasks(data.tasks || []); }
    catch (e) { setTasks([]); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("admin-stats-refresh", h);
    const id = setInterval(load, 30000); // periodic refresh while dashboard is open
    return () => { window.removeEventListener("admin-stats-refresh", h); clearInterval(id); };
  }, [load]);

  if (tasks === null) return null;

  if (tasks.length === 0) {
    return (
      <div data-testid="admin-action-center-clear" className="rounded-2xl border border-[#2E7D52]/30 bg-[#2E7D52]/10 p-4 mb-6 flex items-center gap-3">
        <CheckCircle2 size={18} className="text-[#6FD79E]" />
        <span className="text-sm text-[#D8D2C9]">You're all caught up — no administrator actions require attention right now.</span>
      </div>
    );
  }

  const hasHigh = tasks.some((t) => t.priority === "high");
  const total = tasks.reduce((s, t) => s + t.count, 0);

  const openTask = (t) => {
    setAcknowledged((a) => ({ ...a, [t.key]: true }));
    if (onNavigate) onNavigate(t.section, t.report_category || null);
    else window.location.href = `/admin?section=${t.section}`;
  };

  return (
    <div data-testid="admin-action-center" className={`rounded-2xl border ${hasHigh ? "border-[#EF4444]/40" : "border-[#D4AF37]/25"} bg-[#141113]/80 p-5 mb-6`}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-lg sm:text-xl text-[#FBF8F3] flex items-center gap-2">
          <span className="relative inline-flex">
            <ShieldAlert size={20} className={hasHigh ? "text-[#EF4444]" : "text-[#D4AF37]"} />
            {hasHigh && <span className="aac-pulse-ring" aria-hidden="true" />}
          </span>
          Administrator Actions Required
        </h2>
        <span data-testid="aac-total" className="text-xs font-bold rounded-full h-6 min-w-6 px-2 flex items-center justify-center bg-[#751A32] text-white">{total}</span>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {tasks.map((t) => {
          const pr = PRIORITY[t.priority] || PRIORITY.low;
          const pulse = t.priority === "high" && !acknowledged[t.key];
          return (
            <button
              key={t.key}
              data-testid={`aac-task-${t.key}`}
              onClick={() => openTask(t)}
              className={`text-left rounded-xl border ${pr.ring} bg-[#0C0A0A]/50 p-4 hover:bg-[#1C171A] transition-colors flex items-start gap-3 ${pulse ? "aac-card-pulse" : ""}`}
            >
              <span className="mt-1 relative inline-flex h-3 w-3 shrink-0">
                <span className="inline-flex rounded-full h-3 w-3" style={{ background: pr.dot }} />
                {pulse && <span className="aac-dot-pulse" style={{ background: pr.dot }} aria-hidden="true" />}
              </span>
              <span className="flex-1 min-w-0">
                <span className={`inline-block text-[9px] font-bold tracking-wider rounded px-1.5 py-0.5 mb-1 ${pr.chip}`}>{pr.label}</span>
                <span className="block text-sm font-semibold text-[#FBF8F3]">
                  {t.label} <span className="text-[#D4AF37]">— {t.count}</span>
                </span>
                <span className="block text-[11px] text-[#9E978E] mt-0.5 leading-snug">{t.explanation}</span>
                <span className="inline-flex items-center gap-1 text-[11px] text-[#C9A96E] mt-2 font-medium">
                  Review now <ChevronRight size={12} />
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
